import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import { useVoiceInput } from '../hooks/admin';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MapComponent from '../components/MapComponent';
import { ContactsTab, HistoryTab, CalendarTab, SettingsModal, AddDestinationModal, AlphabetSelectorModal } from '../components/admin';
import MissingPhonesReminder from '../components/admin/MissingPhonesReminder';
import { getApiUrl } from '../utils/api';
import {
  MapPin, Navigation, Clock, Truck, Plus, Trash2, RotateCcw,
  ArrowRight, GripVertical, Lock, Edit2, X,
  Settings, Map, LogOut, Check, Loader2, Users, Phone,
  Share2, CheckCircle, History, Calendar, BookUser
} from 'lucide-react';

const getAPI = () => getApiUrl();

// ─── Sortable Destination Card ───────────────────────────────────────────────
function SortableDestination({ dest, index, isLast, route, destinations, onRemove, onEditNote, fixEndPoint, timeAtStop, startTime, activeId, overId }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dest.id });
  const style = { transform: CSS.Transform.toString(transform), transition: isDragging ? 'none' : transition, zIndex: isDragging ? 1000 : 1 };
  const isFixedEnd = fixEndPoint && isLast;

  const getPreviewIndex = () => {
    if (!isDragging || !overId) return null;
    const overIndex = destinations.findIndex(d => d.id === overId);
    return overIndex === -1 ? null : overIndex + 1;
  };
  const previewIndex = getPreviewIndex();
  const showPreview = isDragging && previewIndex !== null && previewIndex !== (index + 1);
  const isBeingDraggedOver = overId === dest.id && activeId && activeId !== dest.id;

  const getEtaInfo = () => {
    if (!route?.legs) return null;
    let totalMin = 0;
    for (let i = 0; i <= index; i++) {
      if (route.legs[i]) totalMin += route.legs[i].duration_min;
      if (i < index) totalMin += timeAtStop;
    }
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0, 0);
    const arrivalDate = new Date(startDate.getTime() + totalMin * 60 * 1000);
    return { totalMin: Math.round(totalMin), arrivalTime: arrivalDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
  };
  const etaInfo = getEtaInfo();

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'dragging-item' : ''}>
      <div className={`destination-card ${dest.completed ? 'completed' : ''} ${isDragging ? 'is-dragging' : ''} ${isBeingDraggedOver ? 'drag-over' : ''}`} data-testid={`admin-dest-${index}`}>
        <div {...attributes} {...listeners} className={`flex items-center justify-center w-12 min-h-[60px] cursor-grab active:cursor-grabbing hover:bg-[#007aff]/10 rounded-l-xl transition-colors ${isFixedEnd ? 'cursor-not-allowed opacity-50' : ''}`} style={{ touchAction: 'none' }}>
          {isFixedEnd ? <Lock className="w-4 h-4" /> : <GripVertical className="w-5 h-5 text-[#007aff]" />}
        </div>
        <div className="destination-index">
          <span className={`current-number ${showPreview ? 'opacity-0 scale-75' : ''} transition-all duration-150`}>{index + 1}</span>
          {showPreview && <span className="preview-number" style={{ opacity: 1, transform: 'scale(1)' }}>{previewIndex}</span>}
        </div>
        <div className="flex-1 py-2 px-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#1d1d1f]">{dest.postcode}</span>
                {dest.completed && <Check className="w-4 h-4 text-[#34c759] check-animation" />}
                {isFixedEnd && <span className="text-[10px] uppercase text-[#ff9500] font-semibold px-1.5 py-0.5 bg-[#ff9500]/10 rounded">END</span>}
              </div>
              {dest.name && <div className="text-sm text-[#1d1d1f]">{dest.name}</div>}
              {etaInfo && !dest.completed && <div className="text-xs text-[#007aff] mt-0.5 font-medium">ETA: {etaInfo.arrivalTime} ({etaInfo.totalMin} min)</div>}
              {dest.completed && dest.completed_at && (
                <div className="text-xs text-[#34c759] mt-0.5 font-medium">
                  Completed at {new Date(dest.completed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              {dest.note && <div className="text-xs text-[#86868b] italic mt-1">Note: {dest.note}</div>}
            </div>
            <div className="flex items-center gap-1 action-buttons">
              <Button variant="ghost" size="sm" onClick={() => onEditNote(index)} className="p-2 rounded-xl" title="Edit note"><Edit2 className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => onRemove(dest.id)} className="p-2 hover:text-[#ff3b30] rounded-xl" title="Remove"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </div>
      {route?.legs?.[index] && index < destinations.length - 1 && (
        <div className="route-leg">
          <ArrowRight className="w-3 h-3" /><span>{route.legs[index].distance_km} km</span><span className="text-border">|</span><Clock className="w-3 h-3" /><span>{Math.round(route.legs[index].duration_min)} min</span>
        </div>
      )}
    </div>
  );
}

// ─── Admin Dashboard ─────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { user, token, logout } = useAuth();

  // ── Core route state ──
  const [destinations, setDestinations] = useState([]);
  const [route, setRoute] = useState(null);
  const [startLocation, setStartLocation] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [mapCollapsed, setMapCollapsed] = useState(true);
  const [fixEndPoint, setFixEndPoint] = useState(false);
  const [timeAtStop, setTimeAtStop] = useState(5);
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [routeStartedAt, setRouteStartedAt] = useState(null);

  // ── UI state ──
  const [activeTab, setActiveTab] = useState('route');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [startPostcode, setStartPostcode] = useState('');
  const [startLoading, setStartLoading] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [addMode, setAddMode] = useState('contacts'); // 'contacts' | 'search'

  // ── Add destination modal ──
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [destName, setDestName] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);

  // ── Note modal ──
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingNote, setEditingNote] = useState('');

  // ── Contacts ──
  const [contacts, setContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactPostcode, setContactPostcode] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [postcodePickerOpen, setPostcodePickerOpen] = useState(false);
  const [postcodePickerContact, setPostcodePickerContact] = useState(null);

  // ── Alphabet selector ──
  const [alphabetModalOpen, setAlphabetModalOpen] = useState(false);
  const [alphabetStep, setAlphabetStep] = useState(1);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedPostcode, setSelectedPostcode] = useState(null);
  const [alphabetNote, setAlphabetNote] = useState('');
  const [historyTargetId, setHistoryTargetId] = useState(null); // when set, alphabet confirm adds to that history entry

  // ── History ──
  const [routeHistory, setRouteHistory] = useState([]);

  // ── Calendar ──
  const [highlightedDates, setHighlightedDates] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // ── DnD state ──
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  // ── Voice input hook ──
  const { isListening, voiceSupported, startVoiceInput } = useVoiceInput();

  // ── Alphabet helpers ──
  const getContactsByLetter = useCallback(() => {
    const letterMap = {};
    contacts.forEach(contact => {
      const fl = (contact.name || '').charAt(0).toUpperCase();
      if (fl >= 'A' && fl <= 'Z') { if (!letterMap[fl]) letterMap[fl] = []; letterMap[fl].push(contact); }
    });
    return letterMap;
  }, [contacts]);

  const resetAlphabetSelector = useCallback(() => {
    setAlphabetStep(1); setSelectedLetter(null); setSelectedContact(null); setSelectedPostcode(null); setAlphabetNote(''); setHistoryTargetId(null);
  }, []);

  const openAddStopToHistory = useCallback((historyId) => {
    setHistoryTargetId(historyId);
    setAlphabetStep(1); setSelectedLetter(null); setSelectedContact(null); setSelectedPostcode(null); setAlphabetNote('');
    setAlphabetModalOpen(true);
  }, []);

  // ── DnD sensors ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ── WebSocket ──
  const handleWsMessage = useCallback((data) => {
    if (data.type === 'driver_location') setDriverLocation(data.data);
    else if (data.type === 'stop_update') {
      setDestinations(prev => prev.map(d => d.id === data.stop_id ? { ...d, completed: data.completed, note: data.note ?? d.note, completed_at: data.completed_at ?? d.completed_at } : d));
      // Show admin a toast when driver marks a stop completed
      if (data.completed && data.name) {
        const t = data.completed_at ? new Date(data.completed_at) : new Date();
        const timeStr = t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        toast.success(`✓ ${data.name}${data.postcode ? ` (${data.postcode})` : ''} completed`, {
          description: `Marked done at ${timeStr}`,
          duration: 5000
        });
      }
    }
    else if (data.type === 'route_update') setDestinations(data.data?.destinations || []);
    else if (data.type === 'route_started') {
      const t = new Date(data.started_at);
      setStartTime(`${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`);
      setRouteStartedAt(data.started_at);
      toast.info('Driver started the route!');
    }
  }, []);
  const { isConnected } = useWebSocket(token, handleWsMessage);

  // ── Data loading ──
  useEffect(() => {
    const loadRoute = async () => {
      try {
        const res = await axios.get(`${getAPI()}/active-route`);
        setDestinations(res.data.destinations || []);
        setRoute(res.data.route_data);
        setDriverLocation(res.data.driver_location);
        if (res.data.started_at) {
          setRouteStartedAt(res.data.started_at);
          const t = new Date(res.data.started_at);
          setStartTime(`${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`);
        }
        if (res.data.start_location) setStartLocation(res.data.start_location);
        else {
          try {
            const g = await axios.post(`${getAPI()}/geocode`, { postcode: 'Elm Road DA8' });
            setStartLocation({ postcode: g.data.postcode || 'DA8', address: g.data.address, lat: g.data.lat, lng: g.data.lng });
          } catch (e) { console.error('Failed to geocode default start:', e); }
        }
      } catch (e) { console.error('Failed to load route:', e); }
    };
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${getAPI()}/settings`);
        if (res.data.time_at_stop != null) setTimeAtStop(res.data.time_at_stop);
        if (res.data.start_time) setStartTime(res.data.start_time);
        if (res.data.start_location) setStartLocation(res.data.start_location);
      } catch (e) { console.error('Failed to load settings:', e); }
    };
    const loadContacts = async () => { try { const r = await axios.get(`${getAPI()}/contacts`); setContacts(r.data.contacts || []); } catch (e) {} };
    const loadHistory = async () => { try { const r = await axios.get(`${getAPI()}/history`); setRouteHistory(r.data.history || []); } catch (e) {} };
    const loadCalendar = async () => { try { const r = await axios.get(`${getAPI()}/calendar`); setHighlightedDates(r.data.highlighted_dates || []); } catch (e) {} };

    loadRoute(); loadSettings(); loadContacts(); loadHistory(); loadCalendar();
    const poll = setInterval(loadCalendar, 10000);
    return () => clearInterval(poll);
  }, []);

  // ── Save route to backend ──
  const saveRoute = useCallback(async (newDests, newStart = startLocation, newRoute = route) => {
    try {
      await axios.put(`${getAPI()}/active-route`, { destinations: newDests, start_location: newStart, route_data: newRoute });
    } catch (e) { console.error('Failed to save route:', e); toast.error('Failed to sync route'); }
  }, [startLocation, route]);

  // ── Add from alphabet ──
  const addFromAlphabet = useCallback(async (override = null) => {
    const contact = override?.contact || selectedContact;
    const postcode = override?.postcode || selectedPostcode;
    const note = override?.note ?? alphabetNote;
    if (!contact || !postcode) return;

    // Manual entry: auto-save to contact book (create new or append postcode to existing)
    if (override && override.contact) {
      const manualPhone = (override.phone || '').trim();
      try {
        const existing = contacts.find(c => c.name?.toLowerCase() === contact.name.toLowerCase());
        if (existing) {
          const existingPCs = existing.postcodes || (existing.postcode ? [existing.postcode] : []);
          const needsNewPC = !existingPCs.some(p => p.toUpperCase() === postcode.toUpperCase());
          const needsNewPhone = manualPhone && !existing.phone;
          if (needsNewPC || needsNewPhone) {
            const updated = {
              ...existing,
              postcodes: needsNewPC ? [...existingPCs, postcode] : existingPCs,
              postcode: existingPCs[0] || postcode,
              phone: existing.phone || manualPhone
            };
            await axios.post(`${getAPI()}/contacts`, updated);
            setContacts(prev => prev.map(c => c.id === existing.id ? updated : c));
          }
        } else {
          const newContact = { id: crypto.randomUUID(), name: contact.name, postcode, postcodes: [postcode], phone: manualPhone };
          await axios.post(`${getAPI()}/contacts`, newContact);
          setContacts(prev => [...prev, newContact]);
        }
      } catch (e) { /* contact save is best-effort, don't block the route add */ }
    }

    // History mode: append to a past route
    if (historyTargetId) {
      const entry = routeHistory.find(h => h.id === historyTargetId);
      if (!entry) { toast.error('Route not found'); return; }
      const newDest = {
        id: crypto.randomUUID(),
        postcode: postcode,
        name: contact.name,
        completed: true,
        note: note || null
      };
      const updatedDests = [...(entry.destinations || []), newDest];
      try {
        await axios.put(`${getAPI()}/history/${historyTargetId}`, { destinations: updatedDests });
        setRouteHistory(prev => prev.map(r => r.id === historyTargetId ? { ...r, destinations: updatedDests } : r));
        toast.success(`Added ${contact.name} to that route`);
      } catch (e) { toast.error('Failed to add stop'); }
      setAlphabetModalOpen(false); resetAlphabetSelector();
      return;
    }

    // Default: add to current active route
    try {
      const res = await axios.post(`${getAPI()}/geocode`, { postcode: postcode });
      const newDest = { id: crypto.randomUUID(), postcode: res.data.postcode, address: res.data.address, lat: res.data.lat, lng: res.data.lng, name: contact.name, completed: false, note: note || null };
      const updated = [...destinations, newDest];
      setDestinations(updated); setRoute(null); saveRoute(updated, startLocation, null);
      toast.success(`Added ${contact.name} to route`);
      setAlphabetModalOpen(false); resetAlphabetSelector();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add destination'); }
  }, [selectedContact, selectedPostcode, alphabetNote, destinations, startLocation, saveRoute, resetAlphabetSelector, historyTargetId, routeHistory, contacts]);

  // ── Search ──
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ q: query });
      if (startLocation) { params.append('start_lat', startLocation.lat); params.append('start_lng', startLocation.lng); }
      const r = await axios.get(`${getAPI()}/search?${params}`);
      setSearchResults(r.data.results || []);
    } catch (e) { console.error('Search error:', e); }
    finally { setSearchLoading(false); }
  }, [startLocation]);

  useEffect(() => {
    const t = setTimeout(() => searchAddresses(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchAddresses]);

  // ── Auto-save destination to contact book ──
  const autoSaveContact = useCallback(async (name, postcode) => {
    const trimmed = name.trim();
    const pc = postcode.trim().toUpperCase();
    if (!trimmed || !pc) return;

    // Always create a new contact — no auto-merging
    const newContact = { id: crypto.randomUUID(), name: trimmed, postcode: pc, postcodes: [pc], phone: '' };
    try {
      await axios.post(`${getAPI()}/contacts`, newContact);
      setContacts(prev => [...prev, newContact]);
    } catch (e) { console.error('Failed to save contact:', e); }
  }, []);

  // ── Add destination from search ──
  const addDestination = useCallback((result) => {
    if (!destName.trim()) { toast.error('Name is required'); return; }
    const postcode = result.postcode || result.display;
    const newDest = { id: crypto.randomUUID(), postcode, address: result.full_address || result.display, lat: result.lat, lng: result.lng, name: destName.trim(), completed: false, note: null };
    const updated = [...destinations, newDest];
    setDestinations(updated); setRoute(null); saveRoute(updated, startLocation, null);
    setAddModalOpen(false); setSearchQuery(''); setDestName(''); setSearchResults([]); setSelectedResult(null);
    toast.success(`Added: ${destName.trim()}`);
    autoSaveContact(destName.trim(), postcode);
  }, [destinations, destName, startLocation, saveRoute, autoSaveContact]);

  // ── Add from contact ──
  const addFromContact = useCallback(async (contact, selPostcode = null) => {
    const postcodes = contact.postcodes || [contact.postcode];
    if (postcodes.length > 1 && !selPostcode) { setPostcodePickerContact(contact); setPostcodePickerOpen(true); return; }
    const pc = selPostcode || postcodes[0];
    try {
      const res = await axios.post(`${getAPI()}/geocode`, { postcode: pc });
      const newDest = { id: crypto.randomUUID(), postcode: res.data.postcode, address: res.data.address, lat: res.data.lat, lng: res.data.lng, name: contact.name, completed: false, note: null };
      const updated = [...destinations, newDest];
      setDestinations(updated); setRoute(null); saveRoute(updated, startLocation, null);
      toast.success(`Added: ${contact.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to geocode contact'); }
  }, [destinations, startLocation, saveRoute]);

  // ── Contact CRUD ──
  const saveContact = useCallback(async () => {
    if (!contactName.trim()) { toast.error('Name is required'); return; }
    const trimmedName = contactName.trim();
    const newPC = contactPostcode.trim().toUpperCase();

    if (editingContact) {
      // Editing — update name, phone, and add new postcode if provided
      let postcodes = [...(editingContact.postcodes || [editingContact.postcode])];
      if (newPC && !postcodes.some(p => p.toUpperCase() === newPC)) {
        postcodes.push(newPC);
      }
      const data = { ...editingContact, name: trimmedName, postcode: postcodes[0] || '', postcodes, phone: contactPhone.trim() };
      try {
        await axios.post(`${getAPI()}/contacts`, data);
        setContacts(prev => prev.map(c => c.id === editingContact.id ? data : c));
        toast.success('Contact updated');
        setContactModalOpen(false); setEditingContact(null); setContactName(''); setContactPostcode(''); setContactPhone('');
      } catch (e) { toast.error('Failed to update contact'); }
    } else {
      // New contact — always create fresh, no merging
      if (!newPC) { toast.error('Postcode is required'); return; }
      const data = { id: crypto.randomUUID(), name: trimmedName, postcode: newPC, postcodes: [newPC], phone: contactPhone.trim() };
      try {
        await axios.post(`${getAPI()}/contacts`, data);
        setContacts(prev => [...prev, data]);
        toast.success('Contact saved');
        setContactModalOpen(false); setContactName(''); setContactPostcode(''); setContactPhone('');
      } catch (e) { toast.error('Failed to save contact'); }
    }
  }, [contactName, contactPostcode, contactPhone, editingContact]);

  const removePostcodeFromContact = useCallback(async (postcode) => {
    if (!editingContact) return;
    const postcodes = (editingContact.postcodes || [editingContact.postcode]).filter(p => p.toUpperCase() !== postcode.toUpperCase());
    if (postcodes.length === 0) { toast.error('Contact must have at least one postcode'); return; }
    const updated = { ...editingContact, postcodes, postcode: postcodes[0] };
    try {
      await axios.post(`${getAPI()}/contacts`, updated);
      setContacts(prev => prev.map(c => c.id === editingContact.id ? updated : c));
      setEditingContact(updated);
      toast.success(`Removed ${postcode}`);
    } catch (e) { toast.error('Failed to remove postcode'); }
  }, [editingContact]);

  const deleteContact = useCallback(async (id) => {
    try { await axios.delete(`${getAPI()}/contacts/${id}`); setContacts(prev => prev.filter(c => c.id !== id)); toast.success('Contact deleted'); } catch (e) { toast.error('Failed to delete contact'); }
  }, []);

  const mergeContacts = useCallback(async (contactIds) => {
    const selected = contacts.filter(c => contactIds.includes(c.id));
    if (selected.length < 2) return;
    // Merge all postcodes and keep the first phone found
    const allPostcodes = [];
    let phone = '';
    selected.forEach(c => {
      (c.postcodes || [c.postcode]).forEach(pc => {
        if (pc && !allPostcodes.some(p => p.toUpperCase() === pc.toUpperCase())) allPostcodes.push(pc);
      });
      if (!phone && c.phone) phone = c.phone;
    });
    const merged = { ...selected[0], postcodes: allPostcodes, postcode: allPostcodes[0], phone };
    try {
      await axios.post(`${getAPI()}/contacts`, merged);
      for (let i = 1; i < selected.length; i++) {
        await axios.delete(`${getAPI()}/contacts/${selected[i].id}`);
      }
      setContacts(prev => {
        const removedIds = selected.slice(1).map(d => d.id);
        return prev.filter(c => !removedIds.includes(c.id)).map(c => c.id === merged.id ? merged : c);
      });
      toast.success(`Merged ${selected.length} contacts into one`);
    } catch (e) { toast.error('Failed to merge contacts'); }
  }, [contacts]);

  const updateContact = useCallback(async (contact) => {
    await axios.post(`${getAPI()}/contacts`, contact);
    setContacts(prev => prev.map(c => c.id === contact.id ? contact : c));
  }, []);

  // ── Calendar ──
  const toggleCalendarDate = useCallback(async (dateStr) => {
    setHighlightedDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]);
    try { const r = await axios.post(`${getAPI()}/calendar/toggle`, { date: dateStr }); setHighlightedDates(r.data.highlighted_dates || []); }
    catch (e) { try { const r = await axios.get(`${getAPI()}/calendar`); setHighlightedDates(r.data.highlighted_dates || []); } catch (ex) {} }
  }, []);

  // ── History actions ──
  const deleteHistoryRoute = useCallback(async (id) => {
    try { await axios.delete(`${getAPI()}/history/${id}`); setRouteHistory(prev => prev.filter(r => r.id !== id)); toast.success('Route deleted from history'); } catch (e) { toast.error('Failed to delete route'); }
  }, []);
  const updateHistoryDate = useCallback(async (id, newDate) => {
    try { await axios.put(`${getAPI()}/history/${id}`, { completed_at: newDate }); setRouteHistory(prev => prev.map(r => r.id === id ? { ...r, completed_at: newDate, completedAt: newDate } : r)); toast.success('Date updated'); } catch (e) { toast.error('Failed to update date'); }
  }, []);
  const updateHistoryDestinations = useCallback(async (id, destinations) => {
    try {
      await axios.put(`${getAPI()}/history/${id}`, { destinations });
      setRouteHistory(prev => prev.map(r => r.id === id ? { ...r, destinations } : r));
    } catch (e) { toast.error('Failed to update history'); }
  }, []);

  // ── Share ETA ──
  const shareRouteEta = useCallback(async () => {
    if (!route || destinations.length === 0) { toast.error('Calculate a route first'); return; }
    const destsWithEta = destinations.map((dest, i) => {
      let totalMin = 0;
      if (route?.legs) { for (let j = 0; j <= i; j++) { if (route.legs[j]) totalMin += route.legs[j].duration_min; if (j < i) totalMin += timeAtStop; } }
      const [h, m] = startTime.split(':').map(Number);
      const s = new Date(); s.setHours(h, m, 0, 0);
      const a = new Date(s.getTime() + totalMin * 60 * 1000);
      return { postcode: dest.postcode, name: dest.name || '', note: dest.note || '', completed: dest.completed || false, eta: a.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
    });
    try {
      const res = await axios.post(`${getAPI()}/share`, { type: 'route', route_name: 'Delivery Route', start_location: startLocation, start_time: startTime, destinations: destsWithEta, total_distance: route.total_distance_km || 0, total_duration: route.total_duration_min || 0 });
      const shareUrl = `${window.location.origin}/s/${res.data.share_id}`;
      if (navigator.share && navigator.canShare?.({ url: shareUrl })) { try { await navigator.share({ title: 'Delivery ETA', text: 'Check your delivery ETA', url: shareUrl }); return; } catch (e) { if (e.name === 'AbortError') return; } }
      try { await navigator.clipboard.writeText(shareUrl); toast.success('Share link copied to clipboard!'); }
      catch (e) { toast.success(<div className="text-sm"><div className="font-medium mb-1">Share link created!</div><div className="text-xs text-gray-500 break-all">{shareUrl}</div></div>, { duration: 10000 }); }
    } catch (e) { toast.error('Failed to create share link'); }
  }, [route, destinations, timeAtStop, startTime, startLocation]);

  // ── Complete route ──
  const completeRoute = useCallback(async () => {
    try {
      await axios.post(`${getAPI()}/active-route/complete`);
      setDestinations([]); setRoute(null); setStartLocation(null); setDriverLocation(null); setCompleteConfirmOpen(false);
      const h = await axios.get(`${getAPI()}/history`); setRouteHistory(h.data.history || []);
      toast.success('Route completed and saved to history!'); setActiveTab('history');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to complete route'); }
  }, []);

  // ── Remove destination ──
  const removeDestination = useCallback((id) => {
    const updated = destinations.filter(d => d.id !== id);
    setDestinations(updated); setRoute(null); saveRoute(updated, startLocation, null); toast.success('Destination removed');
  }, [destinations, startLocation, saveRoute]);

  // ── DnD ──
  const handleDragStart = useCallback((e) => setActiveId(e.active.id), []);
  const handleDragOver = useCallback((e) => setOverId(e.over?.id || null), []);
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event; setActiveId(null); setOverId(null);
    if (active.id !== over?.id) {
      const oldI = destinations.findIndex(i => i.id === active.id);
      const newI = destinations.findIndex(i => i.id === over.id);
      const reordered = arrayMove(destinations, oldI, newI);
      setDestinations(reordered);
      if (route) {
        setRouteLoading(true);
        try {
          const r = await axios.post(`${getAPI()}/route`, { destinations: reordered.map(d => ({ postcode: d.postcode, lat: d.lat, lng: d.lng, name: d.name })), fix_end_point: false, skip_optimization: true, start_location: startLocation ? { postcode: startLocation.postcode, lat: startLocation.lat, lng: startLocation.lng } : null });
          setRoute(r.data); saveRoute(reordered, startLocation, r.data);
        } catch (e) { console.error('Route update failed:', e); }
        finally { setRouteLoading(false); }
      } else { saveRoute(reordered, startLocation, null); }
    }
  }, [destinations, route, startLocation, saveRoute]);

  // ── Note ──
  const openNoteModal = useCallback((i) => { setEditingIndex(i); setEditingNote(destinations[i]?.note || ''); setNoteModalOpen(true); }, [destinations]);
  const saveNote = useCallback(() => {
    if (editingIndex === null) return;
    const updated = destinations.map((d, i) => i === editingIndex ? { ...d, note: editingNote.trim() || null } : d);
    setDestinations(updated); saveRoute(updated, startLocation, route); setNoteModalOpen(false); toast.success('Note saved');
  }, [editingIndex, editingNote, destinations, startLocation, route, saveRoute]);

  // ── Calculate route ──
  const calculateRoute = useCallback(async () => {
    if (destinations.length < 1) { toast.error('Add at least 1 destination'); return; }
    setRouteLoading(true);
    try {
      const r = await axios.post(`${getAPI()}/route`, { destinations: destinations.map(d => ({ postcode: d.postcode, lat: d.lat, lng: d.lng, name: d.name })), fix_end_point: fixEndPoint, start_location: startLocation ? { postcode: startLocation.postcode, lat: startLocation.lat, lng: startLocation.lng } : null }, { timeout: 120000 });
      const optimized = r.data.optimized_order.map(idx => destinations[idx]);
      setDestinations(optimized); setRoute(r.data); saveRoute(optimized, startLocation, r.data); toast.success('Route optimized!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to calculate route'); }
    finally { setRouteLoading(false); }
  }, [destinations, fixEndPoint, startLocation, saveRoute]);

  // ── Set start location ──
  const setStart = useCallback(async () => {
    if (!startPostcode.trim()) return;
    setStartLoading(true);
    try {
      const r = await axios.post(`${getAPI()}/geocode`, { postcode: startPostcode });
      const ns = { postcode: r.data.postcode, address: r.data.address, lat: r.data.lat, lng: r.data.lng };
      setStartLocation(ns); saveRoute(destinations, ns, null); setRoute(null); setStartPostcode(''); toast.success('Start location set');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to find location'); }
    finally { setStartLoading(false); }
  }, [startPostcode, destinations, saveRoute]);

  // ── Save settings ──
  const saveSettings = useCallback(async () => {
    try { await axios.put(`${getAPI()}/settings`, { start_location: startLocation, time_at_stop: timeAtStop, start_time: startTime }); }
    catch (e) { console.error('Failed to save settings:', e); }
  }, [startLocation, timeAtStop, startTime]);

  // ── Clear route ──
  const clearRoute = useCallback(async () => {
    try { await axios.delete(`${getAPI()}/active-route`); setDestinations([]); setRoute(null); setStartLocation(null); setDriverLocation(null); setClearConfirmOpen(false); toast.success('Route cleared'); }
    catch (e) { toast.error('Failed to clear route'); }
  }, []);

  // ── Contact helpers ──
  const openEditContact = useCallback((c) => { setEditingContact(c); setContactName(c.name); setContactPostcode(''); setContactPhone(c.phone || ''); setContactModalOpen(true); }, []);
  const openNewContact = useCallback(() => { setEditingContact(null); setContactName(''); setContactPostcode(''); setContactPhone(''); setContactModalOpen(true); }, []);

  // ── Derived ──
  const totalDistance = route?.total_distance_km || 0;
  const totalDuration = (route?.total_duration_min || 0) + (destinations.length * timeAtStop);
  const completedCount = destinations.filter(d => d.completed).length;

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════════
  return (
    <div className="app-container" data-testid="admin-dashboard">
      {/* Header */}
      <header className="app-header h-14">
        <div className="logo text-lg">
          <Navigation className="w-6 h-6 text-[#007aff]" />
          <span>Route-Z</span>
          <span className="text-[10px] bg-[#007aff] text-white px-2 py-0.5 ml-2 font-semibold uppercase rounded-md">Admin</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setMapCollapsed(!mapCollapsed)} className="p-2"><Map className={`w-5 h-5 ${mapCollapsed ? 'text-muted-foreground' : 'text-primary'}`} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setSettingsOpen(true)} className="p-2"><Settings className="w-5 h-5" /></Button>
          {destinations.length > 0 && <Button variant="ghost" size="sm" onClick={() => setClearConfirmOpen(true)} className="p-2"><RotateCcw className="w-5 h-5" /></Button>}
          <Button variant="ghost" size="sm" onClick={logout} className="p-2" title="Logout"><LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="flex border-b border-[rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl">
        {[
          { key: 'route', icon: Navigation, count: destinations.length },
          { key: 'contacts', icon: Users, count: contacts.length },
          { key: 'history', icon: History, count: routeHistory.length },
          { key: 'calendar', icon: Calendar, count: highlightedDates.length },
        ].map(({ key, icon: Icon, count }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex-1 py-3 flex items-center justify-center gap-1.5 border-b-2 transition-all duration-200 ${activeTab === key ? 'border-[#007aff] text-[#007aff]' : 'border-transparent text-[#86868b] hover:text-[#1d1d1f]'}`}
            title={key}>
            <Icon className="w-5 h-5" />
            {count > 0 && <span className="text-[10px] bg-[#f5f5f7] px-1.5 py-0.5 rounded-full font-medium">{count}</span>}
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className={`main-grid ${mapCollapsed ? 'map-collapsed' : ''}`}>
        <aside className="sidebar">
          {/* ── Route Tab ── */}
          {activeTab === 'route' && (
            <>
              {/* Inline add mode tabs */}
              <div className="border-b border-[rgba(0,0,0,0.08)]">
                <div className="flex">
                  <button onClick={() => setAddMode('contacts')}
                    className={`flex-1 py-2.5 text-sm font-semibold transition-all duration-200 ${addMode === 'contacts' ? 'text-[#007aff] border-b-2 border-[#007aff]' : 'text-[#86868b] hover:text-[#1d1d1f]'}`}
                    data-testid="add-mode-contacts">
                    <BookUser className="w-4 h-4 inline mr-1.5" />Contacts
                  </button>
                  <button onClick={() => { setAddMode('search'); setAddModalOpen(true); }}
                    className={`flex-1 py-2.5 text-sm font-semibold transition-all duration-200 ${addMode === 'search' ? 'text-[#007aff] border-b-2 border-[#007aff]' : 'text-[#86868b] hover:text-[#1d1d1f]'}`}
                    data-testid="add-mode-search">
                    <Plus className="w-4 h-4 inline mr-1.5" />Manual Input
                  </button>
                </div>

                {/* Contacts: inline alphabet grid */}
                {addMode === 'contacts' && contacts.length > 0 && (
                  <div className="p-3">
                    <div className="grid grid-cols-9 gap-1">
                      {(() => {
                        const letterMap = getContactsByLetter();
                        return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => {
                          const count = letterMap[letter]?.length || 0;
                          const isActive = count > 0;
                          return (
                            <button key={letter} disabled={!isActive}
                              onClick={() => { setSelectedLetter(letter); setAlphabetStep(2); setAlphabetModalOpen(true); }}
                              className={`aspect-square rounded-lg text-xs font-semibold relative transition-all duration-200 ${isActive ? 'bg-[#007aff] text-white hover:bg-[#0066d6] active:scale-95' : 'bg-[#f5f5f7] text-[#c7c7cc] cursor-not-allowed'}`}>
                              {letter}
                              {count > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#ff3b30] text-white text-[8px] rounded-full flex items-center justify-center font-bold">{count}</span>}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
                {addMode === 'contacts' && contacts.length === 0 && (
                  <div className="p-4 text-center text-sm text-[#86868b]">No contacts yet — add some in the Contacts tab</div>
                )}

                {/* Search: opens modal immediately via tab click */}
                {addMode === 'search' && (
                  <div className="p-3 text-center text-sm text-[#86868b]">Search modal open — type a postcode or address</div>
                )}
              </div>

              {route && (
                <div className="border-b border-[rgba(0,0,0,0.08)]">
                  <div className="grid grid-cols-3 gap-[1px] bg-[rgba(0,0,0,0.04)]">
                    <div className="bg-white p-3 text-center"><div className="text-[10px] uppercase text-[#86868b] tracking-wide">Distance</div><div className="font-semibold text-[#1d1d1f]">{totalDistance.toFixed(1)} km</div></div>
                    <div className="bg-white p-3 text-center"><div className="text-[10px] uppercase text-[#86868b] tracking-wide">Duration</div><div className="font-semibold text-[#1d1d1f]">{Math.round(totalDuration)} min</div></div>
                    <div className="bg-white p-3 text-center"><div className="text-[10px] uppercase text-[#86868b] tracking-wide">Done</div><div className="font-semibold text-[#34c759]">{completedCount}/{destinations.length}</div></div>
                  </div>
                </div>
              )}

              <div className="sidebar-scroll">
                {destinations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <MapPin className="w-12 h-12 text-[#86868b] mb-4" />
                    <h3 className="text-lg font-semibold text-[#1d1d1f] mb-2">No destinations</h3>
                    <p className="text-[#86868b] text-sm">Add postcodes to build the driver's route</p>
                  </div>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
                    <SortableContext items={destinations.map(d => d.id)} strategy={verticalListSortingStrategy}>
                      <div className="p-2">
                        {destinations.map((dest, index) => (
                          <SortableDestination key={dest.id} dest={dest} index={index} isLast={index === destinations.length - 1} route={route} destinations={destinations} onRemove={removeDestination} onEditNote={openNoteModal} fixEndPoint={fixEndPoint} timeAtStop={timeAtStop} startTime={startTime} activeId={activeId} overId={overId} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>

              {destinations.length >= 1 && (
                <div className="flex-shrink-0 p-3 pb-4 border-t border-[rgba(0,0,0,0.08)] bg-white space-y-2">
                  <Button onClick={calculateRoute} disabled={routeLoading} className="w-full h-12 text-base font-semibold bg-[#007aff] hover:bg-[#0066d6] rounded-xl transition-all duration-200" data-testid="admin-calc-route-btn">
                    {routeLoading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Calculating...</> : route ? <><Truck className="w-5 h-5 mr-2" />Re-optimize Route</> : <><Truck className="w-5 h-5 mr-2" />Calculate Route</>}
                  </Button>
                  {route && (
                    <div className="flex gap-2">
                      <Button onClick={shareRouteEta} variant="outline" className="flex-1 h-12 text-base font-semibold border-[#007aff] text-[#007aff] hover:bg-[#007aff]/10 rounded-xl transition-all duration-200" data-testid="admin-share-route-btn"><Share2 className="w-5 h-5 mr-2" />Share ETA</Button>
                      <Button onClick={() => setCompleteConfirmOpen(true)} variant="outline" className="flex-1 h-12 text-base font-semibold border-[#34c759] text-[#34c759] hover:bg-[#34c759]/10 rounded-xl transition-all duration-200" data-testid="admin-complete-route-btn"><CheckCircle className="w-5 h-5 mr-2" />Complete</Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Contacts Tab ── */}
          {activeTab === 'contacts' && (
            <ContactsTab contacts={contacts} onAdd={openNewContact} onEdit={openEditContact} onDelete={deleteContact} onAddToRoute={addFromContact} onUpdateContact={updateContact} onMergeContacts={mergeContacts} />
          )}

          {/* ── History Tab ── */}
          {activeTab === 'history' && (
            <HistoryTab routeHistory={routeHistory} deleteHistoryRoute={deleteHistoryRoute} updateHistoryDate={updateHistoryDate} updateHistoryDestinations={updateHistoryDestinations} contacts={contacts} onAddStopToHistory={openAddStopToHistory} />
          )}

          {/* ── Calendar Tab ── */}
          {activeTab === 'calendar' && (
            <CalendarTab highlightedDates={highlightedDates} calendarMonth={calendarMonth} setCalendarMonth={setCalendarMonth} toggleCalendarDate={toggleCalendarDate} />
          )}
        </aside>

        {/* Map */}
        {!mapCollapsed && (
          <div className="map-container" data-testid="admin-map">
            <MapComponent destinations={destinations} route={route} startLocation={startLocation} activeDestination={null} driverLocation={driverLocation} />
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <AddDestinationModal open={addModalOpen} onOpenChange={(open) => { setAddModalOpen(open); if (!open) setAddMode('contacts'); }} searchQuery={searchQuery} setSearchQuery={setSearchQuery} searchResults={searchResults} searchLoading={searchLoading} destName={destName} setDestName={setDestName} selectedResult={selectedResult} setSelectedResult={setSelectedResult} addDestination={addDestination} voiceSupported={voiceSupported} isListening={isListening} startVoiceInput={startVoiceInput} />

      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>Add a note for stop #{(editingIndex ?? 0) + 1}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea placeholder="Enter note..." value={editingNote} onChange={(e) => setEditingNote(e.target.value)} className="min-h-[100px]" data-testid="admin-note-input" autoFocus />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNoteModalOpen(false)}>Cancel</Button>
            <Button onClick={saveNote}>Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} startLocation={startLocation} setStartLocation={setStartLocation} startPostcode={startPostcode} setStartPostcode={setStartPostcode} startLoading={startLoading} onSetStart={setStart} fixEndPoint={fixEndPoint} setFixEndPoint={setFixEndPoint} startTime={startTime} setStartTime={setStartTime} timeAtStop={timeAtStop} setTimeAtStop={setTimeAtStop} driverLocation={driverLocation} setRoute={setRoute} onSave={saveSettings} />

      <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Clear Route?</AlertDialogTitle><AlertDialogDescription>This will remove all destinations and reset the route.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={clearRoute} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Clear</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={completeConfirmOpen} onOpenChange={setCompleteConfirmOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Complete Route?</AlertDialogTitle><AlertDialogDescription>This will save the current route to history and clear it. {completedCount}/{destinations.length} stops completed.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={completeRoute} className="bg-green-600 hover:bg-green-700">Complete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Contact Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription>{editingContact ? 'Update contact details or manage postcodes' : 'Save a frequently used address'}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div><Label className="text-sm font-medium mb-1.5 block">Name <span className="text-destructive">*</span></Label><Input type="text" placeholder="Contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} className="h-12" data-testid="contact-name-input" /></div>

            {/* Existing postcodes when editing */}
            {editingContact && (editingContact.postcodes || [editingContact.postcode]).length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Postcodes</Label>
                <div className="space-y-1.5">
                  {(editingContact.postcodes || [editingContact.postcode]).map((pc, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2.5 bg-[#f5f5f7] rounded-xl">
                      <MapPin className="w-4 h-4 text-[#007aff] flex-shrink-0" />
                      <span className="font-mono text-sm font-medium flex-1">{pc}</span>
                      <Button variant="ghost" size="sm" className="p-1 h-auto hover:text-destructive" onClick={() => removePostcodeFromContact(pc)} title="Remove postcode"><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div><Label className="text-sm font-medium mb-1.5 block">{editingContact ? 'Add Postcode' : 'Postcode'} {!editingContact && <span className="text-destructive">*</span>}</Label><Input type="text" placeholder="e.g. SW1A 1AA" value={contactPostcode} onChange={(e) => setContactPostcode(e.target.value)} className="h-12 font-mono" data-testid="contact-postcode-input" /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Phone (optional)</Label><Input type="tel" placeholder="Phone number" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="h-12" data-testid="contact-phone-input" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setContactModalOpen(false)}>Cancel</Button>
            <Button onClick={saveContact} disabled={!contactName.trim() || (!editingContact && !contactPostcode.trim())}>{editingContact ? 'Update' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Postcode Picker */}
      <Dialog open={postcodePickerOpen} onOpenChange={setPostcodePickerOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Select Postcode</DialogTitle>
            <DialogDescription>{postcodePickerContact?.name} has multiple postcodes. Choose which one to add:</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {(postcodePickerContact?.postcodes || [postcodePickerContact?.postcode]).map((pc, idx) => (
              <Button key={idx} variant="outline" className="w-full h-12 justify-start font-mono text-base" onClick={() => { addFromContact(postcodePickerContact, pc); setPostcodePickerOpen(false); setPostcodePickerContact(null); }}>
                <MapPin className="w-4 h-4 mr-3" />{pc}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlphabetSelectorModal open={alphabetModalOpen} onOpenChange={setAlphabetModalOpen} step={alphabetStep} setStep={setAlphabetStep} selectedLetter={selectedLetter} setSelectedLetter={setSelectedLetter} selectedContact={selectedContact} setSelectedContact={setSelectedContact} selectedPostcode={selectedPostcode} setSelectedPostcode={setSelectedPostcode} alphabetNote={alphabetNote} setAlphabetNote={setAlphabetNote} getContactsByLetter={getContactsByLetter} destinations={destinations} onConfirm={addFromAlphabet} resetSelector={resetAlphabetSelector} isHistoryMode={!!historyTargetId} />

      <MissingPhonesReminder contacts={contacts} onUpdateContact={updateContact} />
    </div>
  );
}
